import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '../../../../../environments/environment';
import { Pole } from "../../../../shared/pole.interface";
import { PageResponse } from "../../../../shared/page-response.interface";

@Injectable({
  providedIn: 'root'
})
export class PillarsService {
  private currentPoleId: number | null = null;

  // Pole ID management methods
  setPoleId(id: number): void {
    this.currentPoleId = id;
  }

  getCurrentPoleId(): number | null {
    return this.currentPoleId;
  }

  clearPoleId(): void {
    this.currentPoleId = null;
  }

  displayedColumns: string[] = ['id', 'username', 'localCommunity', 'address', 'fullName', 'numberOfPositions'];
  dataSource = new MatTableDataSource<any>([]);
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  // ✅ FIXED: Main pillar creation method
  addPillar(pillarData: any): Observable<any> {
    const mappedData = this.mapFormToBackend(pillarData);

    if (!environment.production) {
      console.log('Sending pillar data:', mappedData);
    }

    return this.http.post(`${this.baseUrl}/api/pole`, mappedData);
  }

  // // ✅ FIXED: Corrected method signature and implementation
  // addPoleOperatorRelation(poleId: number, operatorPayload: any): Observable<any> {
  //   // The payload should already be in the correct format from the component
  //   console.log('Service sending operator relation request:', operatorPayload);
  //
  //   return this.http.post(`${this.baseUrl}/api/pole/addPoleOperatorRelation`, operatorPayload, this.getHttpOptions());
  // }

  // ✅ CORRECT - JwtInterceptor handles auth automatically
  addSingleOperatorToPole(poleId: number, operatorData: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/api/pole/${poleId}`,
      operatorData
    );
  }


  addPoleOperatorRelation(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/addPoleOperatorRelation`, payload);
  }

  // ✅ ENHANCED: Better form mapping with proper field validation
  private mapFormToBackend(formData: any) {
    return {
      // Location fields - exact mapping to PoleDTO
      longitude: formData.longitude || '',
      latitude: formData.latitude || '',
      region: formData.region || '',
      municipality: formData.municipality || '',
      settlement: formData.settlement || formData.place || '', // Handle both field names
      street: formData.street || formData.address || '',
      address: formData.address || '',
      nearestHouseNumber: formData.nearestHouseNumber || formData.houseNumber || '',

      // Camera GPS coordinates
      cameraLatitude: formData.cameraLatitude || formData.latitudeCam || '',
      cameraLongitude: formData.cameraLongitude || formData.longitudeCam || '',

      // Technical specifications
      rsuHost: formData.rsuHost || formData.host || '',
      remoteDegree: formData.remoteDegree || '',
      height: this.parseIntSafely(formData.height || formData.heightPillar, 6),
      material: this.mapMaterial(formData.material || formData.pillarMaterial),

      // Set default enum values if not provided
      poleType: formData.poleType || 'LINISKI',
      networkType: formData.networkType || 'COPPER',

      // Condition assessments - handle different input formats
      isProperlyMarkedByMKT: this.mapBooleanValue(formData.isProperlyMarkedByMKT || formData.isMarkedByMKT),
      needsReplacement: this.mapBooleanValue(formData.needsReplacement),
      needsStraighteningReinforcement: this.mapBooleanValue(formData.needsStraighteningReinforcement || formData.needsStraightening),

      // Equipment fields - use correct backend field names
      powerLines: this.mapBooleanValue(formData.powerLines || formData.hasEnergyCables),
      streetLights: this.mapBooleanValue(formData.streetLights || formData.hasStreetLighting),
      equipmentObstructs: this.mapBooleanValue(formData.equipmentObstructs),

      // Cable and equipment counts
      copperBoxCount: this.parseIntSafely(formData.copperBoxCount, 0),
      transitCopperCableCount: this.parseIntSafely(formData.transitCopperCableCount, 0),
      userCopperCableCount: this.parseIntSafely(formData.userCopperCableCount, 0),
      opticalBoxCount: this.parseIntSafely(formData.opticalBoxCount, 0),
      transitOpticalCableCount: this.parseIntSafely(formData.transitOpticalCableCount, 0),
      userOpticalCableCount: this.parseIntSafely(formData.userOpticalCableCount, 0),
      opticalConnectorCount: this.parseIntSafely(formData.opticalConnectorCount, 0),
      hasOpticalReserve: this.mapBooleanValue(formData.hasOpticalReserve),

      // Inspection fields
      inspectionDate: formData.inspectionDate || null,
      inspectorNames: formData.inspectorNames || '',

      // Additional fields
      imageUrl: formData.imageUrl || formData.pillarImage || '',
      note: formData.note || formData.additionalInfo || '',
      cable: this.parseIntSafely(formData.cable, null),
      izvod: this.parseIntSafely(formData.izvod, null),
      parentId: formData.parentId || null
    };
  }

  // ✅ NEW: Get report by region (all operators in region)
  getRegionReport(region: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/api/reports/region/${encodeURIComponent(region)}`
    );
  }

  // ✅ NEW: Get all regions reports
  getAllRegionsReports(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/reports/all-regions`
    );
  }

  // ✅ NEW: Get operator report (operator across all municipalities)
  getOperatorMunicipalitiesReport(operatorId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/api/reports/operator/${operatorId}`
    );
  }

  // ✅ NEW: Get all operators reports
  getAllOperatorsReports(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/reports/all-operators`
    );
  }


  // ✅ ENHANCED: Helper methods for safe data conversion
  private parseIntSafely(value: any, defaultValue: number | null = null): number | null {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    const parsed = parseInt(value.toString(), 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  private mapBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      return lowerValue === 'yes' || lowerValue === 'true' || lowerValue === 'да' || lowerValue === '1';
    }
    return false;
  }

  private mapMaterial(material: string): string {
    if (!material) return 'CONCRETE';

    switch (material.toLowerCase()) {
      case 'betonski':
      case 'concrete':
      case 'beton':
        return 'CONCRETE';
      case 'drven':
      case 'wood':
      case 'drvo':
        return 'WOOD';
      case 'steel':
      case 'chelik':
      case 'челик':
        return 'STEEL';
      default:
        return 'CONCRETE';
    }
  }

  // Search poles by address
  searchPolesByAddress(address: string): Observable<any[]> {
    const params = new HttpParams().set('address', address);
    return this.http.get<any[]>(`${this.baseUrl}/api/pole/search`, {
      params
    });
  }

  getOperatorReport(region: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/api/report/operator`, {
      params: { region }
    });
  }
  downloadDetailedPolesExcel(region: string, userId?: number): Observable<Blob> {
    let params = new HttpParams().set('region', region);
    if (userId) {
      params = params.set('userId', userId.toString());
    }
    return this.http.get(`${this.baseUrl}/api/pole/export/detailed-excel`, {
      params,
      responseType: 'blob'
    });
  }


  userdata(): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/user/userdata`)
  }

  getPillarId(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/pole/get/${id}`);
  }

  updatePillar(pillarData: any): Observable<any> {
    const mappedData = this.mapFormToBackend(pillarData);
    return this.http.put(`${this.baseUrl}/api/pole/updatePole`, mappedData);
  }

  getOperatorsByRegion(region: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/operator/byRegion?region=${encodeURIComponent(region)}`);
  }

  // ✅ DEPRECATED: Use getOperatorsByRegion instead
  getOpratorbyRegion(region: string): Observable<any> {
    console.warn('getOpratorbyRegion is deprecated, use getOperatorsByRegion instead');
    return this.getOperatorsByRegion(region);
  }

  // ✅ ADD this method back to your PillarsService
  addNewOperator(operatorData: any): Observable<any> {
    console.log('Adding new operator:', operatorData);
    return this.http.post(`${this.baseUrl}/api/pole/addPoleOperatorRelation`, operatorData);
  }

  // ✅ Non-paginated method for operator reports (gets ALL poles)
  pillarsFromRegions(region: string): Observable<any[]> {
    console.log('📥 Loading ALL poles for operator report, region:', region);

    return this.http.get<any[]>(
      `${this.baseUrl}/api/pole/from-region-all?region=${encodeURIComponent(region)}`
    );
  }

  getPolesNearby(latitude: number, longitude: number, radiusKm: number = 5): Observable<any[]> {
    const params = new HttpParams()
      .set('latitude', latitude.toString())
      .set('longitude', longitude.toString())
      .set('radiusKm', radiusKm.toString());

    return this.http.get<any[]>(`${this.baseUrl}/api/pole/nearby`, {
      params
    });
  }

  getPolesForMap(region: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/api/pole/map?region=${encodeURIComponent(region)}`);
  }


  getPolesForMapWithBounds(region: string, bounds: any): Observable<any[]> {
    const params = new HttpParams()
      .set('region', region)
      .set('minLat', bounds.minLat)
      .set('maxLat', bounds.maxLat)
      .set('minLng', bounds.minLng)
      .set('maxLng', bounds.maxLng);

    return this.http.get<any[]>(`${this.baseUrl}/api/pole/map`, { params });
  }


  // ✅ Paginated method for other listings (like employee reports)
  pillarsFromRegionsPaginated(
    region: string,
    userId?: number,
    page: number = 0,
    size: number = 10,
    sortBy: string = 'id',
    sortDirection: string = 'asc'
  ): Observable<PageResponse<Pole>> {

    let params = new HttpParams()
      .set('region', region)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (userId !== undefined && userId !== null) {
      params = params.set('userId', userId.toString());
    }

    console.log('📥 Loading paginated poles:', { region, page, size });

    return this.http.get<PageResponse<Pole>>(
      `${this.baseUrl}/api/pole/from-region`,
      { params }
    );
  }

  // ✅ NEW: Universal search method with pagination
  searchPoles(
    searchTerm: string,
    region?: string,
    page: number = 0,
    size: number = 10,
    sortBy: string = 'id',
    sortDirection: string = 'asc'
  ): Observable<PageResponse<Pole>> {

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    if (region && region.trim()) {
      params = params.set('region', region.trim());
    }

    console.log('🔍 Searching poles:', { searchTerm, region, page, size });

    return this.http.get<PageResponse<Pole>>(
      `${this.baseUrl}/api/pole/search`,
      { params }
    );
  }


  // ✅ Paginated method for general use
  getpillarsFromRegionsPaginated(
    region: string,
    page: number = 0,
    size: number = 10,
    sortBy: string = 'id',
    sortDirection: string = 'asc',
    search?: string
  ): Observable<PageResponse<Pole>> {

    let params = new HttpParams()
      .set('region', region)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    console.log('📥 Loading paginated poles:', { region, page, size, search });

    return this.http.get<PageResponse<Pole>>(
      `${this.baseUrl}/api/pole/from-region`,
      { params }
    );
  }

  getOperators(): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/operator`);
  }

  // ✅ ADMIN METHODS
  getUsers(): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/user/getUsers`);
  }

  updateUserStatus(user: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/user/update`, user);
  }

  registerUser(user: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/user/register`, user);
  }

  addOperator(operatorData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/user/saveOperator`, operatorData);
  }

  editOperator(operatorData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/admin/user/opRegions`, operatorData);
  }

  getPoles(): Observable<any> {
    return this.http.get(`${this.baseUrl}/admin/user/getPoles`);
  }

  // ✅ UTILITY METHODS
  deletePoleOperatorRelation(relationId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/pole/operatorRelation/${relationId}`);
  }

  getReportByRegion(region: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/operator/byRegion?region=${encodeURIComponent(region)}`);
  }

  // ✅ DEPRECATED: Use getReportByRegion instead
  getReportbyRegion(region: string): Observable<any> {
    console.warn('getReportbyRegion is deprecated, use getReportByRegion instead');
    return this.getReportByRegion(region);
  }

  downloadPolesExcel(regionOrId: string, type: 'summary' | 'detailed'): Observable<Blob> {
    // Determine if it's region report or operator report
    const isNumeric = !isNaN(Number(regionOrId));

    const url = isNumeric
      ? `${this.baseUrl}/api/reports/export/operator-excel?operatorId=${regionOrId}&type=${type}`
      : `${this.baseUrl}/api/reports/export/region-excel?region=${encodeURIComponent(regionOrId)}&type=${type}`;

    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  // ✅ FIXED: Get employee report with correct URL
  getEmployeeReport(employeeId: number, region?: string): Observable<any> {
    let url = `${this.baseUrl}/api/reports/employee/${employeeId}`;
    if (region) {
      url += `?region=${encodeURIComponent(region)}`;
    }
    return this.http.get<any>(url);
  }
  // ✅ NEW: Download employee Excel report (PUBLIC - make it public!)
  downloadEmployeeExcel(employeeId: number, type: 'summary' | 'detailed', region?: string): Observable<Blob> {
    let url = `${this.baseUrl}/api/reports/export/employee-excel?employeeId=${employeeId}&type=${type}`;
    if (region) {
      url += `&region=${encodeURIComponent(region)}`;
    }
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  // ✅ ADD THIS METHOD - Get operators for a specific pole
  getPoleOperatorsRelation(poleId: number): Observable<any> {
    console.log('📥 Loading operators for pole:', poleId);
    return this.http.get(`${this.baseUrl}/api/pole/getPoleOperatorsRelation/${poleId}`);
  }

updatePole(pilarData: any): Observable<any> {
  return this.http.put(
    `${this.baseUrl}/api/pole/updatePole`,
    pilarData
  );
}


}
